import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { AutomationPreview } from '.';
import { DefaultEditableActionItemContentCSS } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	devElementDataProps,
	numberToUSDCurrencyStringValue,
} from '../../../../models/UiUtils';
import { HwcFrontBackPreview } from '../../../containers/PostcardComposer/HwcFrontBackPreview';
import { HwcTogglePreviewValues } from '../../../containers/PostcardComposer/HwcPreviewToggle';
import { convertHtmlValueToPostcardText } from '../../../containers/PostcardComposer/hooks/usePostcardTextEditor';
import { titles } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../richContent/RichContentDocumentEditor';
import { AutomationStepIcon } from '../../svgs/icons/AutomationStepIcon';
import { Tag } from '../../tags/Tag';
import { styleSheet } from './styles';

interface IAutomationStepPreviewProps {
	template?: Api.IAutomationTemplate;
	/** Default = true */
	renderCustomAutomationPreview?: (templateId: string) => React.ReactNode;
	showPublished?: boolean;
	step?: Api.IAutomationStep;
	steps?: Api.IAutomationStepReference[];
	styles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
}

const DefaultEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS.replace(/color:[ ]?[#]?[a-z0-9]*;/i, `color: ${titles};`),
	minHeight: 14,
	plugins: [], // none
	toolbar: false,
};

export interface IAutomationPreviewStepsContext {
	caseStatement?: Api.IAutomationStepCaseStatement;
	rootTemplate?: Api.IAutomationTemplate;
}

export const AutomationPreviewStepsContext = React.createContext<IAutomationPreviewStepsContext>(null);

const getTrigger = (template?: Api.IAutomationTemplate, published = true) => {
	return (published ? template?.published?.trigger : template?.draft?.trigger) || template?.published?.trigger;
};

export const AutomationPreviewStep: React.FC<React.PropsWithChildren<IAutomationStepPreviewProps>> = props => {
	const {
		template,
		children,
		renderCustomAutomationPreview,
		showPublished = true,
		step,
		steps,
		styles = [],
		impersonationContext,
	} = props;
	const context = React.useContext(AutomationPreviewStepsContext);
	const trigger = getTrigger(context?.rootTemplate, showPublished) || getTrigger(template, showPublished);

	let name = step?._type || '';
	const days = step?.schedule?.numberOfDays || 0;
	const dayCount = Math.abs(days);

	let content: React.ReactNode;
	const onRenderHeader = React.useCallback(
		(stepName = step?._type || '', titleStyle?: StyleDeclarationValue, showDescription = true) => {
			let triggerDescription: string = null;
			switch (trigger?._type) {
				case Api.AutomationTriggerType.Tag: {
					triggerDescription = 'adding the tag';
					break;
				}
				case Api.AutomationTriggerType.ResourceSelector: {
					const t = trigger as Api.IResourceSelectorAutomationTrigger;
					if (
						t.resourceSelector === Api.ResourceSelectorId.HappyBirthday ||
						t.resourceSelector === Api.ResourceSelectorId.Turning65 ||
						t.resourceSelector === Api.ResourceSelectorId.Turning72 ||
						t.resourceSelector === Api.ResourceSelectorId.Turning73
					) {
						triggerDescription = 'the birthday';
					} else if (
						t.resourceSelector === Api.ResourceSelectorId.PolicyRenew ||
						t.resourceSelector === Api.ResourceSelectorId.UpcomingPolicyRenewals
					) {
						triggerDescription = 'renewal';
					} else if (t.resourceSelector === Api.ResourceSelectorId.FinancialReview) {
						triggerDescription = 'the review';
					}
					break;
				}
				default: {
					break;
				}
			}

			const isDoNothing = stepName === 'NoActionAutomationStep';

			stepName = isDoNothing ? 'Do Nothing' : stepName;

			return (
				<div className={css(baseStyleSheet.tableColumnHeader, styleSheet.stepTitle, titleStyle)}>
					{showDescription
						? `${stepName} ${
								days === 0
									? 'same day as '
									: `${dayCount} day${dayCount > 1 ? 's' : ''} ${days < 0 ? 'before' : 'after '}`
							} ${triggerDescription ? ` ${triggerDescription}` : ''}`
						: stepName}
				</div>
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[step, trigger, showPublished]
	);

	const [actionItemContentEditorState, setActionItemContentEditorState] =
		React.useState<Api.IRichContentEditorState>(null);

	const [emailContentEditorState, setEmailContentEditorState] = React.useState<Api.IRichContentEditorState>(null);

	const [textingContentEditorState, setTextingContentEditorState] = React.useState<Api.IRichContentEditorState>(null);
	React.useEffect(() => {
		if (step?._type === 'ActionItemAutomationStep') {
			const s = step as Api.IActionItemAutomationStep;
			setActionItemContentEditorState(convertRawRichTextContentStateToRichContentEditorState(s.content));
		} else if (step?._type === 'EmailAutomationStep' || step?._type === 'HtmlNewsletterAutomationStep') {
			const s = step as Api.IEmailAutomationStep;
			setEmailContentEditorState(convertRawRichTextContentStateToRichContentEditorState(s.content));
		} else if (step?._type === 'TextingAutomationStep') {
			const s = step as Api.IEmailAutomationStep;
			setTextingContentEditorState(convertRawRichTextContentStateToRichContentEditorState(s.content));
		}
	}, [step]);

	const [childTemplateContexts, setChildTemplateContexts] = React.useState<
		Record<
			string,
			{
				templateId: string;
				context?: IAutomationPreviewStepsContext;
			}
		>
	>({});

	React.useEffect(() => {
		if (template) {
			if (step?._type === 'SwitchAutomationStep') {
				const s = step as Api.ISwitchAutomationStep;
				const templates = s.cases?.reduce<
					Record<
						string,
						{
							templateId: string;
							context?: IAutomationPreviewStepsContext;
						}
					>
				>((result, caseStatement) => {
					const templateId = caseStatement.automationTemplateId;

					result[templateId] = {
						context: {
							caseStatement,
							rootTemplate: context?.rootTemplate || template,
						},
						templateId,
					};
					return result;
				}, {});

				setChildTemplateContexts(templates);
			} else {
				setChildTemplateContexts({});
			}
		}
	}, [template, step, context]);

	switch (step?._type) {
		case 'ActionItemAutomationStep': {
			name = 'Action Item';
			const s = step as Api.IActionItemAutomationStep;
			content = !!s.content && (
				<RichContentDocumentEditor
					config={DefaultEditorConfig}
					contentState={actionItemContentEditorState}
					key={`${s.id}-action-item-content`}
					readOnly={true}
					styles={[styleSheet.richContentEditor]}
				/>
			);
			break;
		}
		case 'AddTagAutomationStep':
		case 'RemoveTagAutomationStep': {
			name = step._type === 'AddTagAutomationStep' ? 'Add Tag' : 'Remove Tag';
			const s = step as Api.ITagAutomationStep;
			content = !!s.tagName && <Tag className={css(styleSheet.tag)} tagValue={s.tagName} />;
			break;
		}
		case 'TextingAutomationStep':
		case 'EmailAutomationStep':
		case 'HtmlNewsletterAutomationStep': {
			const texting = step._type === 'TextingAutomationStep';
			name = texting ? 'Texting' : 'Email';
			const s = step as Api.IEmailAutomationStep | Api.ITextingAutomationStep | Api.IHtmlNewsletterAutomationStep;
			content = !!s.content && (
				<RichContentDocumentEditor
					config={DefaultEditorConfig}
					contentState={texting ? textingContentEditorState : emailContentEditorState}
					key={`${s.id}-${step._type}-content`}
					readOnly={true}
					styles={[styleSheet.richContentEditor]}
				/>
			);
			break;
		}
		case 'HandwrittenCardAutomationStep': {
			name = 'Handwritten Card';
			const s = step as Api.HandwrittenCardAutomationStep;
			const templateId = s.templateReference?.templateId;
			if (templateId) {
				content = <HandwrittenCardStepPreview step={s} templateId={templateId} />;
			}
			break;
		}
		case 'OpportunityAutomationStep': {
			const s = step as Api.IOpportunityAutomationStep;
			name = 'Ceate opportunity';
			content = numberToUSDCurrencyStringValue(s.dealSize ?? 0);
			break;
		}
		case 'SwitchAutomationStep': {
			const s = step as Api.ISwitchAutomationStep;
			if (!childTemplateContexts) {
				return null;
			}
			return (
				<>
					{s.cases?.map((caseStatement, index) => {
						const templateId = caseStatement.automationTemplateId;

						const child = childTemplateContexts[templateId];
						if (!child) {
							return null;
						}
						// handle the display name for the segment
						let caseStatementName = `${
							caseStatement.name || `Segment ${index + 1}${caseStatement.isDefault ? ' (everyone else)' : ''}`
						}`;
						if (
							!caseStatement.name &&
							!caseStatement.isDefault &&
							(Api.VmUtils.Automations.triggers.isResourceSelectorTrigger(
								trigger,
								Api.ResourceSelectorId.PolicyRenew
							) ||
								Api.VmUtils.Automations.triggers.isResourceSelectorTrigger(
									trigger,
									Api.ResourceSelectorId.UpcomingPolicyRenewals
								))
						) {
							const sorted = Api.VmUtils.sortContactFilterCriteria(caseStatement?.filter);
							const filter = sorted.filters[0];
							const policyCriteria = filter?.criteria?.find(
								x => x.property === Api.ContactFilterCriteriaProperty.Policy
							);
							if (policyCriteria) {
								caseStatementName = `${caseStatementName}: ${policyCriteria.value}`;
							}
						}
						return (
							<AutomationPreviewStepsContext.Provider key={templateId} value={child.context}>
								<div className={css(...styles)} {...devElementDataProps({ id: templateId })}>
									<div className={css(styleSheet.segmentStepHeader)}>
										<div className={css(styleSheet.stepIndicator)}>
											<AutomationStepIcon
												className={css(styleSheet.stepIndicatorIcon)}
												key={step.id}
												type={step._type as Api.AutomationStepModelType}
											/>
										</div>
										{onRenderHeader(caseStatementName, styleSheet.stepTitleNoMargin, false)}
									</div>
									{renderCustomAutomationPreview ? (
										renderCustomAutomationPreview?.(child.templateId) || null
									) : (
										<AutomationPreview
											key={templateId}
											showPublished={showPublished}
											templateId={child.templateId}
											impersonationContext={impersonationContext}
										/>
									)}
									{children}
								</div>
							</AutomationPreviewStepsContext.Provider>
						);
					})}
				</>
			);
		}
		default: {
			break;
		}
	}

	const isLast = !!steps && step === steps[steps.length - 1];
	const hideLine = context?.caseStatement ? isLast && !!context?.caseStatement.isDefault : isLast;
	return (
		<div className={css(styleSheet.step, ...styles)}>
			<div className={css(styleSheet.stepIndicator)}>
				<AutomationStepIcon
					className={css(styleSheet.stepIndicatorIcon)}
					key={step.id}
					type={step._type as Api.AutomationStepModelType}
				/>
				{!hideLine && <div className={css(styleSheet.stepIndicatorLine)} />}
			</div>
			<div className={css(styleSheet.stepContent)}>
				{onRenderHeader(name)}
				{content}
				{children}
			</div>
		</div>
	);
};

interface IHandwrittenCardStepPreviewProps {
	step: Api.HandwrittenCardAutomationStep;
	templateId: string;
}

const HandwrittenCardStepPreview: React.FC<IHandwrittenCardStepPreviewProps> = ({ step, templateId }) => {
	const userSession = useUserSession();
	const [toggleValue, setToggleValue] = React.useState<HwcTogglePreviewValues>('image');
	const handwritingStyle =
		step.style ??
		userSession.user?.userPreferences?.handwrittenCardPreferences?.handwritingStyle ??
		Api.HandwritingStyle.Aladdin;
	const signature = step.excludeSignature
		? ''
		: (step.signature ?? userSession.user?.userPreferences?.handwrittenCardPreferences?.signature ?? '');
	const postcardText = convertHtmlValueToPostcardText(step.content?.document ?? '');
	return (
		<HwcFrontBackPreview
			templateId={templateId}
			value={toggleValue}
			onValueChange={setToggleValue}
			handwritingStyle={handwritingStyle}
			signature={signature}
			postcardText={postcardText}
		/>
	);
};
